import React, { useEffect, useState } from "react";
import { Card, Drawer, Menu, Table } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import utils from "utils";
import _ from "lodash";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import Flex from "../../../../components/shared-components/Flex";
import dayjs from "dayjs";
import { expandCartBundles } from "../../../../functions/reports";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";

const CustomersReport = ({ orders = [], dateRange, dateRangeText, loading }) => {
  const { t } = useTranslation();
  const { products } = useProducts();
  const [customersData, setCustomersData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerProducts, setSelectedCustomerProducts] = useState([]);

  // Process orders data into customers data with proper date filtering
  useEffect(() => {
    if (orders && orders.length > 0 && dateRange && products) {
      // Apply date filtering (same logic as getSalesDashboardData in reports.js)
      const start = parseInt(dayjs(dateRange[0]).startOf("day").format("x"));
      const end = parseInt(dayjs(dateRange[1]).endOf("day").format("x"));

      const filteredByDate = orders.filter((order) => {
        const deliveryDate = dayjs(order.farmorderDeliveryDate).format("DD-MM-YYYY");
        const orderTimestamp = parseInt(dayjs(deliveryDate, "DD-MM-YYYY").format("x"));
        return orderTimestamp >= start && orderTimestamp <= end;
      });

      // Group orders by customer and calculate totals
      const customerGroups = _.groupBy(filteredByDate, "farmorderCustomerId");
      const customersArray = Object.entries(customerGroups).map(([customerId, customerOrders]) => {
        const totalOrderValue = customerOrders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
        const orderCount = customerOrders.length;
        const avgOrderValue = orderCount > 0 ? totalOrderValue / orderCount : 0;
        const customerName = customerOrders[0].farmorderCustomerName;

        return {
          customerId,
          customerName,
          totalOrderValue,
          orderCount,
          avgOrderValue,
          orders: customerOrders,
        };
      });

      // Sort by total order value (highest first)
      const sortedCustomers = _.orderBy(customersArray, ["totalOrderValue"], ["desc"]);
      setCustomersData(sortedCustomers);
    } else {
      setCustomersData([]);
    }
  }, [orders, dateRange, products]);

  const showCustomerDetails = (customer) => {
    // Process all products for this customer
    const allProducts = {};

    customer.orders.forEach((order) => {
      if (order.userShoppingCart) {
        // Expand bundles
        const expandedCart = expandCartBundles(order.userShoppingCart, products || []);

        expandedCart.forEach((product) => {
          const productId = product.productId;
          if (allProducts[productId]) {
            allProducts[productId].totalQuantity += product.itemCount;
            allProducts[productId].totalValue += product.productPrice * product.itemCount;
          } else {
            allProducts[productId] = {
              productId,
              productName: product.productName,
              totalQuantity: product.itemCount,
              totalValue: product.productPrice * product.itemCount,
            };
          }
        });
      }
    });

    const productsArray = Object.values(allProducts);
    const sortedProducts = _.orderBy(productsArray, ["totalValue"], ["desc"]);

    setSelectedCustomer(customer);
    setSelectedCustomerProducts(sortedProducts);
    setDrawerVisible(true);
  };

  const dropdownMenu = (customer) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => showCustomerDetails(customer)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </a>
      </Menu.Item>
    </Menu>
  );

  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => {
      setSelectedCustomer(null);
      setSelectedCustomerProducts([]);
    }, 300);
  };

  // Table columns for customers
  const customersColumns = [
    {
      title: t("customer.name"),
      dataIndex: "customerName",
      render: (customerName, customer) => (
        <button className={"text-button"} onClick={() => showCustomerDetails(customer)}>
          <span style={{ display: "block" }}>{customerName}</span>
        </button>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "customerName"),
    },
    {
      title: t("order_value"),
      dataIndex: "totalOrderValue",
      render: (value) => <span>{CURRENCY + formatPrice(value)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalOrderValue"),
    },
    {
      title: t("number_of_orders"),
      dataIndex: "orderCount",
      sorter: (a, b) => utils.antdTableSorter(a, b, "orderCount"),
    },
    {
      title: t("average_order_value"),
      dataIndex: "avgOrderValue",
      render: (value) => <span>{CURRENCY + formatPrice(value)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "avgOrderValue"),
    },
    {
      title: "",
      render: (_, customer) => (
        <div
          className="text-right"
          style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "3px" }}
        >
          <EllipsisDropdown menu={dropdownMenu(customer)} />
        </div>
      ),
    },
  ];

  // Table columns for customer products in drawer
  const productsColumns = [
    {
      title: t("product_name"),
      dataIndex: "productName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
    },
    {
      title: t("total_quantity"),
      dataIndex: "totalQuantity",
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalQuantity"),
    },
    {
      title: t("total_value"),
      dataIndex: "totalValue",
      render: (value) => <span>{CURRENCY + formatPrice(value)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalValue"),
    },
  ];

  return (
    <>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <span className="text-muted">{dateRangeText}</span>
        </div>
        <div className="table-responsive">
          <Table
            showSorterTooltip={false}
            columns={customersColumns}
            dataSource={customersData}
            rowKey="customerId"
            loading={loading}
            pagination={{ showSizeChanger: true }}
            scroll={{ x: 600 }}
          />
        </div>
      </Card>

      <Drawer
        title={selectedCustomer ? `${selectedCustomer.customerName} - ${t("products_ordered_by_customer")}` : ""}
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width={800}
      >
        {selectedCustomer && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("total_order_value")}: </strong>
              {CURRENCY + formatPrice(selectedCustomer.totalOrderValue)}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("number_of_orders")}: </strong>
              {selectedCustomer.orderCount}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("average_order_value")}: </strong>
              {CURRENCY + formatPrice(selectedCustomer.avgOrderValue)}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t("date_range")}: </strong>
              {dateRangeText}
            </div>
            <Table
              showSorterTooltip={false}
              columns={productsColumns}
              dataSource={selectedCustomerProducts}
              rowKey="productId"
              pagination={false}
              scroll={{ x: 400 }}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default CustomersReport;
